import axios from "axios";
import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { apiClient } from "./api";

export const tweeter_backendUrl = import.meta.env.PROD
  ? import.meta.env.VITE_REACT_APP_TWEETER_FASTAPI_URL_PRODUCTION
  : import.meta.env.VITE_REACT_APP_TWEETER_FASTAPI_URL_DEVELOPMENT;

// ==========================================
// Types for Twitter API
// ==========================================

export interface TweetRating {
  username: string;
  rating: number;
  feedback?: string;
}

export interface InstagramContentMetadata {
  posted: boolean;
  posted_at: string; // ISO 8601 timestamp
  post_id: string;
}

export interface GeneratedContent {
  date: string;
  doc_id: string;
  emoji: string;
  generation_model: string;
  hashtags: string[];
  persona_id: string;
  sources: string[];
  /** IDs of images to be posted with this tweet, if any */
  images: string[];
  text: string;
  deleted?: boolean;
  feedback?: string;
  rating?: number;
  ratings?: TweetRating[];
  instagram?: InstagramContentMetadata;
  posted?: boolean;
  posted_at?: string; // ISO 8601 timestamp
  twitter_post_id?: string;
}

export interface PendingReply {
  doc_id: string;
  original_message: {
    id: string;
    text: string;
    sender_id: string;
    timestamp: string;
    user_details?: {
      username?: string;
      name?: string;
      profile_image_url?: string;
      location?: string;
      created_at?: string;
      followers_count?: number;
      verified?: boolean;
    };
  };
  response_content: string;
  thread_context: string[];
  created_at: string;
  media_urls: string[];
  status: "pending" | "approved" | "rejected" | "skipped" | "spam";
  prompt?: string;
  metadata?: {
    reason?: string;
    rule_violated?: string;
    risk_factors?: string[];
    confidence?: number;
  };
  reason?: string;
  rule_violated?: string;
}

type ContentPage = { data: GeneratedContent[]; cursor?: string | null };
interface PendingRepliesResponse {
  data: PendingReply[];
}

// ==========================================
// API functions for Twitter account management
// ==========================================

export async function initiateTwitterLogin(
  personaId: string,
  redirectUrl: string,
): Promise<string> {
  try {
    const response = await axios.get(`${tweeter_backendUrl}/twitter/login`, {
      params: {
        persona_id: personaId,
        redirect_url: redirectUrl,
      },
    });

    const { authorization_url } = response.data;
    if (!authorization_url) {
      throw new Error("Authorization URL not returned from the server.");
    }

    return authorization_url;
  } catch (error: any) {
    console.error(
      `Error initiating Twitter login for agent ${personaId}: ${error.response?.data || error.message}`,
      error,
    );
    throw error;
  }
}

export async function initiateTwitterLogout(
  personaId: string,
): Promise<string> {
  try {
    const response = await apiClient.post(
      `/api/personas/${personaId}/twitter/logout`,
    );

    return response.data;
  } catch (error: any) {
    console.error(
      `Error initiating Twitter logout for agent ${personaId}: ${error.response?.data || error.message}`,
      error,
    );
    throw error;
  }
}

// ==========================================
// API functions for Twitter Tweets
// ==========================================

export async function fetchLatestGeneratedContent(
  personaId: string,
  authenticated = true,
  cursor: string | null,
): Promise<ContentPage> {
  try {
    const url = authenticated
      ? `/api/tweets/fetch_latest_tweets`
      : `/api/tweets/fetch_latest_tweets/public`;
    const response = await apiClient.get(url, {
      params: {
        persona_id: personaId,
        limit: 10,
        cursor,
      },
    });

    const json = response.data;

    return { data: json.data, cursor: json.pagination?.cursor };
  } catch (e) {
    console.error(`Error fetching latest tweets for ${personaId}`, e);
    throw e;
  }
}

export async function updateTweet(
  docId: string,
  updatedData: Partial<GeneratedContent>,
): Promise<void> {
  try {
    const response = await apiClient.put(`/api/tweets/${docId}`, updatedData);
    if (response.status !== 200) {
      throw new Error("Failed to update tweet");
    }
  } catch (error: any) {
    console.error(`Error updating tweet with doc_id ${docId}:`, error);
    throw error;
  }
}

export async function deleteTweet(
  docId: string,
  updatedData: Partial<GeneratedContent>,
): Promise<void> {
  try {
    const response = await apiClient.put(
      `/api/tweets/${docId}/delete`,
      updatedData,
    );
    if (response.status !== 200) {
      throw new Error("Failed to delete tweet");
    }
  } catch (error) {
    console.error(`Error deleting tweet with doc_id ${docId}:`, error);
    throw error;
  }
}

// ==========================================
// API functions for Twitter reply management - USING NEW ROUTER
// ==========================================

export async function getPendingReplies(
  persona_id: string,
  limit: number = 20,
): Promise<PendingRepliesResponse> {
  try {
    // Use the original endpoint structure
    const response = await apiClient.get(`/api/twitter-replies`, {
      params: {
        persona_id,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching pending replies:", error);
    throw error;
  }
}

export async function getSkippedReplies(
  persona_id: string,
  limit: number = 20,
): Promise<PendingRepliesResponse> {
  try {
    // Keep the original endpoint structure
    const response = await apiClient.get(`/api/twitter-replies/skipped`, {
      params: {
        persona_id,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching skipped replies:", error);
    throw error;
  }
}

export async function approveReply(
  doc_id: string,
  persona_id: string,
  edited_response?: string,
): Promise<{ message: string; tweet_id?: string }> {
  try {
    const payload = {
      persona_id,
      edited_response,
    };

    // Use the original endpoint structure
    const response = await apiClient.post(
      `/api/twitter-replies/approve/${doc_id}`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error approving reply:", error);
    throw error;
  }
}

export async function rejectReply(
  doc_id: string,
  persona_id: string,
  reason?: string,
): Promise<{ message: string }> {
  try {
    const payload = {
      persona_id,
      reason,
    };

    // Use the original endpoint structure
    const response = await apiClient.post(
      `/api/twitter-replies/reject/${doc_id}`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error rejecting reply:", error);
    throw error;
  }
}

export async function respondToMessage(
  persona_id: string,
  message_id: string,
): Promise<{
  success: boolean;
  message: string;
  persona_id: string;
  message_id: string;
  automated: boolean;
}> {
  try {
    const payload = {
      persona_id,
      message_id,
    };

    // Use the original endpoint structure
    const response = await apiClient.post(
      "/api/twitter-replies/process-message",
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error processing message:", error);
    throw error;
  }
}

// ==========================================
// React Query hooks for Twitter functionality
// ==========================================

export function useLatestGeneratedContentQuery(
  personaId: string,
  authenticated = true,
) {
  return useInfiniteQuery({
    queryKey: tweetQueries.latestGeneratedContent(personaId, authenticated)
      .queryKey,
    queryFn: ({ pageParam }) =>
      fetchLatestGeneratedContent(personaId, authenticated, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.cursor,
  });
}

export function usePendingRepliesQuery(
  personaId: string,
  limit: number = 20,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["pending-replies", personaId],
    queryFn: async () => {
      return await getPendingReplies(personaId, limit);
    },
    enabled: !!personaId && enabled,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useSkippedRepliesQuery(
  personaId: string,
  limit: number = 20,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["skipped-replies", personaId],
    queryFn: async () => {
      return await getSkippedReplies(personaId, limit);
    },
    enabled: !!personaId && enabled,
    refetchInterval: 300000, // Refetch every 5 minutes
  });
}

// For spam, we'll just create a derived hook that filters the skipped replies data
export function useSpamRepliesData(
  skippedRepliesData: PendingRepliesResponse | undefined,
) {
  return useMemo(() => {
    if (!skippedRepliesData || !skippedRepliesData.data) return { data: [] };

    // Filter just the spam messages from skipped replies
    const spamReplies = skippedRepliesData.data.filter(
      (reply) =>
        reply.status === "spam" ||
        reply.metadata?.rule_violated === "spam_detection",
    );

    return { data: spamReplies };
  }, [skippedRepliesData]);
}

export function useApproveReplyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      docId,
      personaId,
      editedResponse,
    }: {
      docId: string;
      personaId: string;
      editedResponse?: string;
    }) => {
      return await approveReply(docId, personaId, editedResponse);
    },
    onSuccess: (_, variables) => {
      // Invalidate pending replies query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["pending-replies", variables.personaId],
      });
    },
  });
}

export function useRejectReplyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      docId,
      personaId,
      reason,
    }: {
      docId: string;
      personaId: string;
      reason?: string;
    }) => {
      return await rejectReply(docId, personaId, reason);
    },
    onSuccess: (_, variables) => {
      // Invalidate pending replies query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ["pending-replies", variables.personaId],
      });
    },
  });
}

export async function getRepliedMessages(
  persona_id: string,
  limit: number = 10,
  next_cursors?: Record<string, string> | null,
  message_types: string[] = [
    "reply",
    "mention",
    "influencer_engagement",
    "pending_reply",
  ],
): Promise<{
  data: PendingReply[];
  pagination: {
    cursors: Record<string, string>;
    has_more: boolean;
  };
}> {
  try {
    const params: any = {
      persona_id,
      limit,
      message_types: message_types.join(","),
    };

    // Add next_cursors if provided
    if (next_cursors && Object.keys(next_cursors).length > 0) {
      params.next_cursors = JSON.stringify(next_cursors);
    }

    const response = await apiClient.get(`/api/twitter-replies/replied`, {
      params,
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching replied messages:", error);
    throw error;
  }
}

export function useRepliedMessagesQuery(
  personaId: string,
  limit: number = 50,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["replied-messages", personaId],
    queryFn: async () => {
      return await getRepliedMessages(personaId, limit);
    },
    enabled: !!personaId && enabled,
    refetchInterval: 60000, // Reduced from 300000 (5 minutes)
  });
}

export async function postTweet(tweetId: string) {
  try {
    await apiClient.post(`/api/tweets/${tweetId}/post`);
  } catch (error) {
    console.error(`Error posting tweet with doc_id ${tweetId}:`, error);
    throw error;
  }
}

export type GenerateTweetInput = {
  personaId: string;
  subject?: string;
  tone: string;
  maxResults: number;
  dataSource: "knowledge_base_only" | "knowledge" | "all";
  username?: string;
};

export async function generateTweet({
  personaId,
  subject = "",
  tone,
  maxResults,
  dataSource,
  username,
}: GenerateTweetInput) {
  const payload = {
    persona_id: personaId,
    user_request: subject,
    tone: tone,
    max_results: maxResults,
    job_type: "one-off",
    auto_post: "generate-only",
    data_source: dataSource,
    user_id: username,
  };

  try {
    await axios.post(`${tweeter_backendUrl}/generate_tweets`, payload);
  } catch (error) {
    console.error("Error generating tweet:", error);
    throw error;
  }
}

export const tweetQueries = {
  latestGeneratedContent: (personaId: string, authenticated = true) => ({
    queryKey: ["tweets", personaId, authenticated],
  }),
};
